/* eslint-disable max-len */
import userApi from '_api/user'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useModal } from '_hooks/useModal'

import { formatDate } from '_utils/function'
import { createAddressString, findMatchingAddress } from '../utils/productTransforms'

/**
 * Owns the buyer's saved delivery addresses and the two address modals.
 * Display helpers are memoized since they're recomputed on every render of
 * the original component - here they only recompute when their real inputs
 * (selected address, saved addresses, edit-order address) change.
 */
const useAddressBook = ({ userId, edittingOrder, editOrderBookingAddress, editOrderDeliveryDate }) => {
  const [userAddresses, setUserAddresses] = useState([])
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null)

  const {
    isOpen: isAddressModalOpen,
    openModal: handleOpenAddNewAddress,
    closeModal: closeAddressModal,
    toggleModal: toggleAddressModal,
  } = useModal()

  const {
    isOpen: isChangeAddressModalOpen,
    openModal: openChangeAddressModal,
    closeModal: closeChangeAddressModal,
    toggleModal: toggleChangeAddressModal,
  } = useModal()

  const refreshAddresses = useCallback(async () => {
    try {
      const response = await userApi.listUserAddress()
      setUserAddresses(response?.addresses || [])
      return response?.addresses || []
    } catch (err) {
      setUserAddresses([])
      return []
    }
  }, [])

  useEffect(() => {
    if (userId) {
      refreshAddresses()
    }
  }, [userId, refreshAddresses])

  const displayAddress = useMemo(() => {
    if (selectedDeliveryAddress) {
      return createAddressString(selectedDeliveryAddress)
    }
    if (edittingOrder && editOrderBookingAddress) {
      return editOrderBookingAddress
    }
    const defaultAddress = userAddresses.find((addr) => addr.isDefault) || userAddresses[0]
    return defaultAddress ? createAddressString(defaultAddress) : null
  }, [selectedDeliveryAddress, edittingOrder, editOrderBookingAddress, userAddresses])

  const selectedAddressForModal = useMemo(() => {
    if (selectedDeliveryAddress) {
      return selectedDeliveryAddress
    }
    if (edittingOrder && editOrderBookingAddress) {
      const matchingAddress = findMatchingAddress(editOrderBookingAddress, userAddresses)
      if (matchingAddress) {
        return { ...matchingAddress, deliveryDate: editOrderDeliveryDate || matchingAddress.deliveryDate }
      }
      return { address: editOrderBookingAddress, deliveryDate: editOrderDeliveryDate }
    }
    return userAddresses.find((addr) => addr.isDefault) || userAddresses[0]
  }, [selectedDeliveryAddress, edittingOrder, editOrderBookingAddress, editOrderDeliveryDate, userAddresses])

  const displayDeliveryDate = useMemo(() => {
    const d =
      (selectedDeliveryAddress && selectedDeliveryAddress.deliveryDate) ||
      (edittingOrder && editOrderDeliveryDate) ||
      null
    if (!d) return null
    if (typeof d === 'string') return d
    return formatDate({ date: d })
  }, [selectedDeliveryAddress, edittingOrder, editOrderDeliveryDate])

  return {
    userAddresses,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    isAddressModalOpen,
    isChangeAddressModalOpen,
    toggleAddressModal,
    toggleChangeAddressModal,
    openChangeAddressModal,
    closeAddressModal,
    closeChangeAddressModal,
    handleOpenAddNewAddress,
    refreshAddresses,
    displayAddress,
    selectedAddressForModal,
    displayDeliveryDate,
  }
}

export default useAddressBook
